import { createQueryKeyStore } from '@lukemorales/query-key-factory';

import { fetchAssetDetails } from '~modules/media-library/controller/api/assetDetails';
import { fetchAutoComplete, fetchAutoCompleteForFreeTags } from '~modules/media-library/controller/api/autoComplete';
import { fetchCollection } from '~modules/media-library/controller/api/collection';
import { fetchCropFormats, fetchCroppedFormat } from '~modules/media-library/controller/api/cropFormats';
import { fetchDownloadFormat, fetchDownloadFormats } from '~modules/media-library/controller/api/downloadFormats';
import { fetchDownloadVariations } from '~modules/media-library/controller/api/downloadVariations';
import { fetchRaiseRequestFields } from '~modules/media-library/controller/api/raiseRequestForm';
import { fetchSearchResults } from '~modules/media-library/controller/api/search';
import { fetchSettings } from '~modules/media-library/controller/api/settings';
import { type AssetId } from '~modules/media-library/model/Asset';
import { makeAsset } from '~modules/media-library/model/AssetFactory';
import { makeCollection } from '~modules/media-library/model/CollectionFactory';
import { makeCropFormatList } from '~modules/media-library/model/CropFactory';
import { type CropDownloadDto } from '~modules/media-library/model/CropFormat';
import { makeDownloadFormats } from '~modules/media-library/model/DownloadFactory';
import { makeDownloadVariations } from '~modules/media-library/model/DownloadFactory';
import { makeRaiseRequest } from '~modules/media-library/model/RaiseRequestAssets';
import { makeSearchResult } from '~modules/media-library/model/SearchResultFactory';
import { makeSettings } from '~modules/media-library/model/SettingsFactory';

export const queries = createQueryKeyStore({
  mediaLibrary: {
    details: (assetId: string) => ({
      queryKey: [assetId],
      queryFn: async () => makeAsset(await fetchAssetDetails(assetId)),
    }),
    raiseRequestFormFields: (assetId: string) => ({
      queryKey: [assetId],
      queryFn: async () => makeRaiseRequest(await fetchRaiseRequestFields(assetId)),
    }),
    collectionDetails: (assetId: string) => ({
      queryKey: [assetId],
      queryFn: async () => makeCollection(await fetchCollection(assetId)),
    }),
    cropFormats: (assetId: string) => ({
      queryKey: [assetId],
      queryFn: async () => makeCropFormatList(await fetchCropFormats(assetId)),
    }),
    croppedAsset: (options: CropDownloadDto) => ({
      queryKey: [options],
      queryFn: async () => makeDownloadFormats(await fetchCroppedFormat(options)),
    }),
    downloadFormats: (assetId: string) => ({
      queryKey: [assetId],
      queryFn: async () => makeDownloadFormats(await fetchDownloadFormats(assetId)),
    }),
    downloadFormat: (assetId: string, key: string) => ({
      queryKey: [{ assetId, key }],
      queryFn: async () => makeDownloadFormats(await fetchDownloadFormat(assetId, key)),
    }),
    downloadVariations: (assetId: string) => ({
      queryKey: [assetId],
      queryFn: async () => makeDownloadVariations(await fetchDownloadVariations(assetId)),
    }),

    settings: (realm: string, idCollection?: AssetId) => ({
      queryKey: [{ realm, idCollection }],
      queryFn: async () => makeSettings(await fetchSettings(realm, idCollection)),
    }),
    search: (realm: string, query: string, idCollection?: AssetId) => ({
      queryKey: [{ realm, query, idCollection }],
      queryFn: async ({ pageParam }) => {
        return makeSearchResult(await fetchSearchResults(realm, query, idCollection, pageParam as number));
      },
    }),
    autoComplete: (query: string) => ({
      queryKey: [{ query }],
      queryFn: () => fetchAutoComplete(query),
    }),
    scrollRestore: (idCollection: AssetId) => ({
      queryKey: [idCollection],
      queryFn: async () => {
        return new Promise<Element>((resolve, reject) => {
          const element = document.querySelector(`[data-assetid=${idCollection}`);
          if (!element) {
            reject();
            return;
          }
          resolve(element);
        });
      },
    }),
    scrollCollection: (idCollection: AssetId) => ({
      queryKey: [idCollection],
      queryFn: async () => {
        return new Promise<Element>((resolve, reject) => {
          const element = document.querySelector('.ml--collection-header');
          if (!element) {
            reject();
            return;
          }
          resolve(element);
        });
      },
    }),
  },
  contribute: {
    autoComplete: (query: string) => ({
      queryKey: [{ query }],
      queryFn: () => fetchAutoCompleteForFreeTags(query),
    }),
  },
});
